import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Menu,
  Sun,
  Moon,
  Bell,
  LogOut,
  User,
  Settings,
  Key,
  ChevronLeft,
  ChevronRight,
  Building2,
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { mockNotifications } from '../../data/mockData';

import { Logo } from '../common/Logo';

export const Navbar = ({ isCollapsed, onToggle, hasTopBanner = false }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const unreadCount = mockNotifications.filter((n) => !n.read).length;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    const safeName = (name || 'User').trim();
    return safeName
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'super_admin':
        return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300';
      case 'wl_admin':
        return 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300';
      case 'employee':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300';
      case 'agent':
        return 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300';
      default:
        return '';
    }
  };

  const getRoleLabel = (role) => {
    const labels = { super_admin: 'Super Admin', wl_admin: 'WL Admin', employee: 'Employee', agent: 'Agent' };
    return labels[role] || role;
  };

  const getProfilePath = (role) => {
    switch (role) {
      case 'super_admin': return '/admin/profile';
      case 'wl_admin': return '/whitelabel/profile';
      case 'employee': return '/employee/profile';
      case 'agent': return '/agent/profile';
      default: return '/admin/profile';
    }
  };

  const getProfilePasswordPath = (role) => {
    switch (role) {
      case 'super_admin': return '/admin/profile/password';
      case 'wl_admin': return '/whitelabel/profile/password';
      case 'employee': return '/employee/profile/password';
      case 'agent': return '/agent/profile'; // agent may not have password route; adjust if needed
      default: return '/admin/profile/password';
    }
  };

  const getSettingsPath = (role) => {
    switch (role) {
      case 'super_admin': return '/admin/settings/company';
      case 'wl_admin': return '/whitelabel/profile/security';
      case 'employee': return '/employee/profile';
      case 'agent': return '/agent/profile';
      default: return '/admin/settings/company';
    }
  };

  const getHomePath = (role) => {
    switch (role) {
      case 'super_admin': return '/admin/dashboard';
      case 'wl_admin': return '/whitelabel/dashboard';
      case 'employee': return '/employee/dashboard';
      case 'agent': return '/agent/dashboard';
      default: return '/';
    }
  };

  return (
    <nav className={`fixed left-0 right-0 h-16 bg-background border-b border-border z-50 transition-theme ${hasTopBanner ? 'top-12' : 'top-0'}`}>
      <div className="h-full flex items-center justify-between px-4">
        {/* Left Section */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggle}
            className="flex"
            data-testid="sidebar-toggle-btn"
          >
            <Menu className="h-5 w-5 md:hidden" />
            <div className="hidden md:block">
              {isCollapsed ? (
                <ChevronRight className="h-5 w-5" />
              ) : (
                <ChevronLeft className="h-5 w-5" />
              )}
            </div>
          </Button>

          <Link to={getHomePath(user?.role)} data-testid="logo-link">
            <Logo size="md" />
          </Link>
        </div>

        {/* Right Section */}
        <div className="flex items-center gap-2">
          {/* Theme Toggle */}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className="rounded-full"
            data-testid="theme-toggle-btn"
          >
            {theme === 'dark' ? (
              <Sun className="h-5 w-5" />
            ) : (
              <Moon className="h-5 w-5" />
            )}
          </Button>

          {/* Notifications */}
          {/* {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative rounded-full"
                  data-testid="notifications-btn"
                >
                  <Bell className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">
                      {unreadCount}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <DropdownMenuLabel className="flex items-center justify-between">
                  Notifications
                  <Badge variant="secondary">{unreadCount} new</Badge>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {mockNotifications.slice(0, 5).map((notification) => (
                  <DropdownMenuItem
                    key={notification.id}
                    className="flex flex-col items-start gap-1 p-3 cursor-pointer"
                  >
                    <div className="flex items-center gap-2 w-full">
                      <span
                        className={`h-2 w-2 rounded-full ${notification.read ? 'bg-muted' : 'bg-emerald-500'
                          }`}
                      />
                      <span className="font-medium text-sm flex-1">
                        {notification.title}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground pl-4">
                      {notification.message}
                    </p>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem className="justify-center text-sm font-medium text-emerald-600">
                  View all notifications
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )} */}

          {/* User Menu */}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="null"
                  className="flex items-center gap-2 pl-2 pr-3"
                  data-testid="user-menu-btn"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user.avatar} alt={user.full_name || user.name} />
                    <AvatarFallback>{getInitials(user.full_name || user.name)}</AvatarFallback>
                  </Avatar>
                  <div className="hidden md:flex flex-col items-start">
                    <span className="text-sm font-medium">{user.full_name || user.name}</span>
                    <span
                      className={`text-xs px-1.5 py-0.5 rounded-full ${getRoleBadgeColor(
                        user.role
                      )}`}
                    >
                      {getRoleLabel(user.role)}
                    </span>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span>{user.full_name || user.name}</span>
                    <span className="text-xs text-muted-foreground font-normal">
                      {user.email}
                    </span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild data-testid="profile-menu-item">
                  <Link to={getProfilePath(user?.role)}>
                    <User className="mr-2 h-4 w-4" />
                    Profile
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="password-menu-item">
                  <Link to={getProfilePasswordPath(user?.role)}>
                    <Key className="mr-2 h-4 w-4" />
                    Change Password
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="settings-menu-item">
                  <Link to={getSettingsPath(user?.role)}>
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-red-600"
                  data-testid="logout-menu-item"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button asChild data-testid="login-btn">
              <Link to="/login">Login</Link>
            </Button>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
